import React, { memo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { buildLegalDocumentPath } from "../../utils/legal/legalStatus";
import { useLegalUpdate } from "./useLegalUpdate";

/**
 * App-blocking legal-update modal for the dashboard.
 *
 * When the signed-in user has an outstanding required action on a legal
 * document (e.g. an acknowledgement of updated Terms), this overlay covers the
 * dashboard: the user must either acknowledge and continue, or exit (which
 * signs them out). Because acknowledgement is recorded server-side, the overlay
 * reappears on the next sign-in until they agree.
 *
 * It is suppressed on the read-only `/legal/*` document pages so the user can
 * review the updated documents (which carry their own "Agree and continue"),
 * and across `/auth/*` so public auth validation and redirects are not masked
 * by an app-blocking overlay.
 *
 * @param {{ onExit: () => void }} props - `onExit` signs the user out.
 */
function LegalUpdateOverlay({ onExit }) {
  const { t } = useTranslation(["common", "legal"]);
  const location = useLocation();
  const {
    outstandingDocuments,
    hasBlockingUpdate,
    submitting,
    error,
    acknowledgeAll,
    declineAll,
  } = useLegalUpdate();

  // Exit = decline: record a declined action for the audit trail, then sign out.
  // The user stays blocked until they agree on a later sign-in.
  const handleExit = async () => {
    await declineAll();
    onExit();
  };

  const handleAcknowledge = async () => {
    await acknowledgeAll();
  };

  if (
    location.pathname.startsWith("/legal") ||
    location.pathname.startsWith("/auth")
  ) {
    return null;
  }

  if (!hasBlockingUpdate || outstandingDocuments.length === 0) {
    return null;
  }

  const documentNames = outstandingDocuments
    .map((document) => document.title)
    .filter(Boolean)
    .join(", ");

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-update-title"
    >
      <div className="w-full max-w-md rounded-2xl bg-container p-6 shadow-xl flex flex-col gap-4">
        <h2 id="legal-update-title" className="title-text">
          {t("overlay.title", {
            ns: "legal",
            defaultValue: "We've updated our legal terms",
          })}
        </h2>

        <p className="text-greyscale-2 leading-relaxed">
          {t("overlay.body", {
            ns: "legal",
            documents: documentNames,
            defaultValue:
              "We've made changes to {{documents}}. Please agree to continue, or exit to sign out.",
          })}
        </p>

        <div className="flex flex-col gap-1">
          {outstandingDocuments.map((document) => (
            <Link
              key={document.documentType}
              to={buildLegalDocumentPath(document.documentType)}
              className="link-text w-fit"
            >
              {t("overlay.review", {
                ns: "legal",
                document: document.title,
                defaultValue: "Review the updated {{document}}",
              })}
            </Link>
          ))}
        </div>

        {error ? <p className="text-sm text-greyscale-2">{error}</p> : null}

        <div className="flex flex-col gap-2">
          <button
            type="button"
            className="primary-button"
            onClick={handleAcknowledge}
            disabled={submitting}
          >
            {submitting
              ? t("overlay.saving", { ns: "legal", defaultValue: "Saving…" })
              : t("overlay.agree", {
                  ns: "legal",
                  defaultValue: "Agree and continue",
                })}
          </button>
          <button
            type="button"
            className="outlined-button"
            onClick={handleExit}
            disabled={submitting}
          >
            {t("overlay.exit", { ns: "legal", defaultValue: "Exit" })}
          </button>
        </div>
      </div>
    </div>
  );
}

export default memo(LegalUpdateOverlay);
